import { useState } from "react";
import { clsx } from "clsx";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Check,
  ChevronDown,
  ChevronUp,
  GripVertical,
  RotateCcw,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { DLC_BADGE_CHIP } from "@/utils/dlc";

export interface ReorderSection {
  label: string;
  badge?: { text: string; cls: string };
}

interface Props {
  /* Sections the user can see, in their current (already ordered) order. */
  sections: ReorderSection[];
  /* Called with the new full label order after every move. */
  onChange: (labels: string[]) => void;
  /* Drops the custom order and falls back to the default one. */
  onReset: () => void;
  /* Leaves reorder mode. */
  onDone: () => void;
  /* True when the user has a custom order — enables the "restore defaults" button. */
  isCustomised: boolean;
}

/* One draggable section row. Up/down buttons mirror the drag for keyboard and
   touch users, for whom dragging in a narrow sidebar is awkward. */
function Row({
  section,
  index,
  total,
  onMove,
}: {
  section: ReorderSection;
  index: number;
  total: number;
  onMove: (from: number, to: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.label });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={clsx(
        "flex items-center gap-1 px-2 py-1 mx-2 border-l-2 border-transparent",
        "bg-[var(--color-bg-elevated)] mb-0.5",
        isDragging && "opacity-60 border-[var(--color-primary)]",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Przeciągnij sekcję ${section.label}`}
        className="flex-shrink-0 cursor-grab touch-none text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
      >
        <GripVertical size={13} />
      </button>
      {section.badge && (
        <span className={clsx(DLC_BADGE_CHIP, section.badge.cls)}>
          <span className="relative z-10">{section.badge.text}</span>
        </span>
      )}
      {/* title= — the row is narrower than a header (grip + two arrows), so long
          names truncate here even at the sidebar header size. */}
      <span
        title={section.label}
        className="sidebar-section-label flex-1 min-w-0 truncate"
      >
        {section.label}
      </span>
      <button
        type="button"
        disabled={index === 0}
        onClick={() => onMove(index, index - 1)}
        title="W górę"
        aria-label={`Przenieś sekcję ${section.label} w górę`}
        className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] disabled:opacity-25 disabled:hover:text-[var(--color-text-muted)] transition-colors"
      >
        <ChevronUp size={13} />
      </button>
      <button
        type="button"
        disabled={index === total - 1}
        onClick={() => onMove(index, index + 1)}
        title="W dół"
        aria-label={`Przenieś sekcję ${section.label} w dół`}
        className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] disabled:opacity-25 disabled:hover:text-[var(--color-text-muted)] transition-colors"
      >
        <ChevronDown size={13} />
      </button>
    </div>
  );
}

/* Reorder mode for the sidebar: replaces the nav links with a compact,
   drag-and-drop list of the section headers the user has access to. */
export function SidebarSectionReorder({
  sections,
  onChange,
  onReset,
  onDone,
  isCustomised,
}: Props) {
  const [confirmReset, setConfirmReset] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const labels = sections.map((s) => s.label);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= labels.length || from === to) return;
    onChange(arrayMove(labels, from, to));
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    move(labels.indexOf(String(active.id)), labels.indexOf(String(over.id)));
  };

  return (
    <div className="py-1">
      <p className="px-4 pb-2 text-[10px] font-mono text-[var(--color-text-muted)] leading-tight">
        Przeciągnij sekcje albo użyj strzałek, aby ustawić własną kolejność
        menu.
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={labels} strategy={verticalListSortingStrategy}>
          {sections.map((s, i) => (
            <Row
              key={s.label}
              section={s}
              index={i}
              total={sections.length}
              onMove={move}
            />
          ))}
        </SortableContext>
      </DndContext>

      <div className="flex items-center gap-1.5 px-2 pt-3">
        <button
          type="button"
          onClick={onDone}
          className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-mono uppercase tracking-wide bg-[var(--color-primary)] text-[var(--color-text-inverse)] hover:opacity-90 transition-opacity"
        >
          <Check size={12} />
          Gotowe
        </button>
        <button
          type="button"
          onClick={() => setConfirmReset(true)}
          disabled={!isCustomised}
          title="Przywróć domyślną kolejność sekcji"
          className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-mono uppercase tracking-wide border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
        >
          <RotateCcw size={12} />
          Domyślne
        </button>
      </div>

      <Modal
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Przywrócić domyślną kolejność?"
        size="sm"
      >
        <p className="text-sm text-[var(--color-text-secondary)]">
          Twoja własna kolejność sekcji menu zostanie usunięta, a menu wróci do
          układu domyślnego. Tej operacji nie można cofnąć.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={() => setConfirmReset(false)}
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-wide border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => {
              onReset();
              setConfirmReset(false);
            }}
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-wide bg-[var(--color-primary)] text-[var(--color-text-inverse)] hover:opacity-90 transition-opacity"
          >
            Przywróć domyślne
          </button>
        </div>
      </Modal>
    </div>
  );
}
